import { useEffect, useState } from "react";

type RevealPhase = "loading" | "typing" | "complete";

function useSimulatedReveal(text: string, loadingMs = 520) {
  const [phase, setPhase] = useState<RevealPhase>("loading");
  const [visibleLength, setVisibleLength] = useState(0);

  useEffect(() => {
    setPhase("loading");
    setVisibleLength(0);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let typingTimer: number | undefined;
    const loadingTimer = window.setTimeout(() => {
      if (reducedMotion) {
        setVisibleLength(text.length);
        setPhase("complete");
        return;
      }
      setPhase("typing");
      const charactersPerTick = Math.max(2, Math.ceil(text.length / 95));
      typingTimer = window.setInterval(() => {
        setVisibleLength((current) => {
          const next = Math.min(text.length, current + charactersPerTick);
          if (next === text.length) {
            if (typingTimer !== undefined) window.clearInterval(typingTimer);
            setPhase("complete");
          }
          return next;
        });
      }, 18);
    }, loadingMs);

    return () => {
      window.clearTimeout(loadingTimer);
      if (typingTimer !== undefined) window.clearInterval(typingTimer);
    };
  }, [loadingMs, text]);

  return { phase, visibleLength };
}

function ThinkingDots() {
  return <span className="thinking" aria-hidden="true"><i /><i /><i /></span>;
}

export function SimulatedAiText({ text }: { text: string }) {
  const { phase, visibleLength } = useSimulatedReveal(text);
  return (
    <p className="simulated-ai-text" aria-busy={phase !== "complete"}>
      <span className="sr-only" aria-live="polite">{phase === "complete" ? text : "Preparing AI summary"}</span>
      <span aria-hidden="true">
        {phase === "loading" ? <ThinkingDots /> : <>{text.slice(0, visibleLength)}{phase === "typing" && <span className="typing-cursor" />}</>}
      </span>
    </p>
  );
}

export function SimulatedExecutiveSummary({ lead, highlights }: { lead: string; highlights: string[] }) {
  const combined = [lead, ...highlights].join("\n");
  const { phase, visibleLength } = useSimulatedReveal(combined);
  const visibleLead = lead.slice(0, Math.min(lead.length, visibleLength));
  let consumedLength = lead.length + 1;
  const visibleHighlights = highlights.map((highlight) => {
    const visible = highlight.slice(0, Math.max(0, Math.min(highlight.length, visibleLength - consumedLength)));
    consumedLength += highlight.length + 1;
    return visible;
  }).filter(Boolean);
  const cursor = phase === "typing" && <span className="typing-cursor" />;

  return (
    <div className="summary-content simulated-ai-summary" aria-busy={phase !== "complete"}>
      <span className="sr-only" aria-live="polite">{phase === "complete" ? [lead, ...highlights].join(". ") : "Preparing AI summary"}</span>
      <div aria-hidden="true">
        {phase === "loading"
          ? <p><ThinkingDots /></p>
          : <>
            <p>{visibleLead}{visibleHighlights.length === 0 && cursor}</p>
            {visibleHighlights.length > 0 && <ul>{visibleHighlights.map((highlight, index) => <li key={index}>{highlight}{index === visibleHighlights.length - 1 && cursor}</li>)}</ul>}
          </>}
      </div>
    </div>
  );
}

export function SimulatedAiPre({ text }: { text: string }) {
  const { phase, visibleLength } = useSimulatedReveal(text, 520);
  return (
    <pre className="simulated-ai-pre" aria-busy={phase !== "complete"}>
      <span className="sr-only" aria-live="polite">{phase === "complete" ? text : "Generating tailored resume"}</span>
      <span aria-hidden="true">
        {phase === "loading" ? <ThinkingDots /> : <>{text.slice(0, visibleLength)}{phase === "typing" && <span className="typing-cursor" />}</>}
      </span>
    </pre>
  );
}

export function SimulatedInsightContent({ title, description }: { title: string; description: string }) {
  const combined = `${title}\n${description}`;
  const { phase, visibleLength } = useSimulatedReveal(combined, 420);
  const visibleTitle = title.slice(0, Math.min(title.length, visibleLength));
  const descriptionLength = Math.max(0, visibleLength - title.length - 1);
  const visibleDescription = description.slice(0, descriptionLength);

  if (phase === "loading") {
    return <div className="insight-ai-loading" role="status" aria-label="Preparing AI insight"><ThinkingDots /></div>;
  }

  return (
    <>
      <div aria-hidden="true">
        <h2>{visibleTitle}{phase === "typing" && visibleLength <= title.length && <span className="typing-cursor" />}</h2>
        <p>{visibleDescription}{phase === "typing" && visibleLength > title.length && <span className="typing-cursor" />}</p>
      </div>
      <span className="sr-only" aria-live="polite">{phase === "complete" ? `${title}. ${description}` : "Preparing AI insight"}</span>
    </>
  );
}
